import { DeserializedApplication } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Button, Container, Spinner } from '@amzn/awsui-components-react';
import { Data, Layout } from 'plotly.js';
import PlotlyBasic from 'plotly.js-basic-dist';
import { createRef } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';

import { constants } from '../../../common/Localization';
import { useResize } from '../../../common/resize-hook';
import { commonGraphConfig, generateDistributions } from '../../../common/util';

const PlotlyChart = createPlotlyComponent(PlotlyBasic);

interface Props {
    loading: boolean;
    applications: DeserializedApplication[];
    rankExist: boolean;
    getStarted: () => void;
}

const noAppPrioritization = (getStarted: () => void): JSX.Element => (
    <Container data-testid='no-app-prioritization'>
        <Box textAlign='center' className='landing-container'>
            <h1>{constants.APP_PRIORITIZATION_TITLE}</h1>
            <p>
                {constants.APP_PRIORITIZATION_DESCRIPTION}
            </p>
            <Box padding={{top: 'xl'}}>
                <Button variant='primary' onClick={getStarted} id='btn-getstarted-prioritization'>
                    {constants.GET_STARTED_BUTTON_TEXT}
                </Button>
            </Box>
        </Box>
    </Container> 
);

const LoadingSpinner = (
    <Container>
        <Box textAlign='center' className='landing-container'>
            <Spinner size='big' />
        </Box>
    </Container>
);

function ApplicationPrioritizationGraph({ loading, applications, rankExist, getStarted, }: Props): JSX.Element {
    const componentRef = createRef<HTMLDivElement>();
    const graphDimensions = useResize(componentRef);
    if (loading) {
        return LoadingSpinner;
    }
    if (!rankExist) {
        return noAppPrioritization(getStarted);
    }

    // Find the minimum and maximum scores, and generate normalized scores
    let min = applications[0].score;
    let max = applications[0].score;
    const appScores = applications.reduce((scores: number[], app: DeserializedApplication) => {
        min = app.score < min ? app.score : min;
        max = app.score > max ? app.score : max;
        scores.push(app.score || 0);
        return scores;
    }, []);

    const normalizedScores = appScores.map((score: number) => {
        if (min === max) return 0.9; // when min and max are equal the score may be greater than 1
        else if (score === max) return 0.9;
        else return (score - min)/(max - min);
    });
    const distributions = generateDistributions(normalizedScores, 0, 1, 10);
    
    const graph_data: Data[] = [
        {
            type: 'bar',
            hoverinfo: 'y',
            textposition: 'auto',
            x: distributions.map(dist => dist.lowerLimit),
            y: distributions.map(dist => dist.count),
        }
    ];

    const layout: Partial<Layout> = {
        bargap: 0.5,
        height: 600,
        width: graphDimensions.width,
        showlegend: false,
        yaxis: {
            fixedrange: true,
            tickformat: ',d',
        },
        xaxis: {
            fixedrange: true,
            showticklabels: true,
            tickvals: [0, 0.9],
            ticktext: [constants.APP_PRIORITIZATION_GRAPH_LOW_PRIOR, constants.APP_PRIORITIZATION_GRAPH_HIGH_PRIOR],
        },
        margin: { t: 10, l: 20, r: 20, b: 50 },
        title: {
            text: `<b>${constants.APP_PRIORITIZATION_GRAPH_TITLE}</b>`,
            x: 0,
            y: 600,
            xanchor: 'left',
            yanchor: 'top',
        }, 
    };
    return (
        <Container>
            <div ref={componentRef}>
                <PlotlyChart data={graph_data} layout={layout} config={commonGraphConfig}/>
            </div>
        </Container>
    );   
}

export default ApplicationPrioritizationGraph;
